import { useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { cn } from '@/lib/shadcn/utils';
import { startLinkedInLogin } from '@/modules/auth/linkedinSso';
import {
  authButtonClickedEvents,
  type AuthIntent,
} from '@/modules/auth/types/externalLogin';
import { Button } from '@/modules/ui/components/button/Button';

import { LinkedInLogo } from './LinkedInLogo';

export interface LinkedInLoginProps {
  returnTo?: string;
  /** True once the Terms Notice beside the button shows its link. */
  acceptTerms: boolean;
  /** Whether the callback reports `userSignedUp` or `userSignedIn`. */
  intent: AuthIntent;
  onError: (error?: Error) => void;
  /** LinkedIn's filled blue button, for in-app browsers where it's the main way in. */
  prominent?: boolean;
  /** Runs on click before the redirect, e.g. to report where it was chosen. */
  onClick?: () => void;
}

export const LinkedInLogin = ({
  returnTo,
  acceptTerms,
  intent,
  onError,
  prominent = false,
  onClick,
}: LinkedInLoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const trackEvent = useTrackEvent();

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      onClick?.();
      trackEvent(authButtonClickedEvents[intent], { authMethod: 'LinkedIn' });
      trackEvent('linkedInSSO', {
        stage: 'started',
      });

      await startLinkedInLogin(returnTo, acceptTerms, intent);
    } catch (error) {
      setIsLoading(false);
      onError(error instanceof Error ? error : undefined);
    }
  };

  return (
    <Button
      variant="outline"
      loading={isLoading}
      onClick={handleLogin}
      // Matches Google's large button beside it: 40px tall, 4px corners, the
      // icon at the left edge and the label centred.
      className={cn(
        'relative h-10 w-full rounded-[4px] font-medium active:scale-[0.98]',
        prominent &&
          'h-12 rounded-lg border-[#0A66C2] bg-[#0A66C2] text-[15px] font-semibold text-white hover:bg-[#004182] hover:text-white',
      )}
    >
      {!isLoading && (
        <LinkedInLogo
          className={cn(
            'size-4 text-[#0A66C2]',
            prominent ? 'text-white' : 'absolute left-3',
          )}
        />
      )}
      {intent === 'signUp' ? 'Sign up with LinkedIn' : 'Continue with LinkedIn'}
    </Button>
  );
};
